import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { cloneDeep, isEmpty } from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import * as callActions from "../../actions/callActions";
import * as contractActions from "../../actions/contractActions";
import * as userActions from "../../actions/userActions";
import * as customerActions from "../../actions/customerActions";
import * as projectActions from "../../actions/projectActions";
import * as codeActions from "../../actions/codeActions";
import * as modalActions from "../../actions/modalActions";
import * as serviceActions from "../../actions/serviceActions";
import * as supportTicketActions from "../../actions/supportTicketActions";

import CallForm from "./CallForm";
import HeaderWrapper from "../common/HeaderWrapper";
import Loading from "../common/Loading";
import ConfirmModal from "../common/ConfirmModal";
import Modal from "../common/Modal";
import Checkbox from "../common/Checkbox";
import SupportTicketDisplay from "../support-ticket/SupportTicketDisplay";
import Badge from "../common/Badge";
import SupportTicketForm from "../support-ticket/SupportTicketForm";
import { customToast as toast } from "../../utils";

const CallEditPage = ({
  actions,
  customers,
  contracts,
  projects,
  users,
  services,
  call,
  history,
  supportTicket,
  auth,
  codes,
  savingCall,
  isMobile
}) => {
  const { id } = useParams();
  const [editing, setEditing] = useState(false);
  const [state, setState] = useState({
    note: ""
  });
  const [recurring, setRecurring] = useState(false);
  const [isSupportTicket, setIsSupportTicket] = useState(false);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sendCustomerEmail, setSendCustomerEmail] = useState(true);
  const [createFollowUp, setCreateFollowUp] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await actions.getCall(id);

      setLoading(false);
    };
    if (id) {
      fetchData();
    }
  }, [id, actions]);

  useEffect(() => {
    if (!customers.length) actions.loadCustomers();
    if (!contracts.length) actions.loadContracts();
    if (!projects.length) actions.loadProjects();
    if (!services.length) actions.loadServices();
    if (!users.length) actions.loadUsers();
  }, [
    actions,
    contracts.length,
    customers.length,
    projects.length,
    services.length,
    users.length
  ]);

  useEffect(
    () => () => {
      actions.clearCall();
      actions.clearRecurrence();
      actions.clearSupportTicket();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (call) {
      actions.getRecurrence(call.recurring_id, false);
      setState(call);
      setRecurring(call.recurring_id);
      setIsSupportTicket(false);
      if (call.support_ticket_id) {
        actions.getSupportTicket(call.support_ticket_id);
        setIsSupportTicket(true);
      }
    }
  }, [actions, call]);

  const onClickEdit = () => {
    if (!editing && call.recurring_id) {
      actions.requestCallId(call._id);
    } else {
      setEditing((prev) => !prev);
    }
  };

  const onCancel = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("ref")) {
      history.push(`${params.get("ref")}`);
    } else {
      history.goBack();
    }
  };

  const onSaveNewCall = async (newCall) => {
    actions.clearCall();
    setState(null);

    try {
      const createdCall = await actions.createCall(newCall);
      history.push(`/app/calls/${createdCall._id}`);
      await actions.getCall(createdCall._id);
      actions.hideModalSuccess("supportTicketModalAddCall");
    } catch (err) {
      toast.error("Error adding call to ticket. Please contact support.");
    }
  };

  const confirmSingle = () => {
    const newCall = cloneDeep(call);
    newCall.recurring_id = null;
    actions.updateCall(newCall);
    actions.hideModal();
    setEditing(true);
    setRecurring(false);
  };

  const confirmSeries = () => {
    actions.getRecurringCalls(call.recurring_id);
    actions.hideModal();
    setEditing(true);
    setRecurring(true);
  };

  const followUpCall = () => {
    setCreateFollowUp(true);
    actions.showModalSuccess("supportTicketModalAddCall");
  };

  // check if we can resolve the ticket
  const checkTicketCalls = async () => {
    const { calls } = await actions.loadCalls({
      isPopulated: true,
      filtered: { support_ticket_id: [supportTicket._id] }
    });
    let close = true;
    if (Array.isArray(calls)) {
      calls.forEach((ticketCall) => {
        if (ticketCall.stage.order < 4) {
          close = false;
        }
      });
    }

    if (close) {
      actions.showModalSuccess("resolveOrFollowUpTicketModal");
    } else {
      actions.showModalSuccess("createFollowUpCallModal");
    }
  };

  const handleSave = async (newCall) => {
    setLoading(true);
    newCall._id = call._id;

    const stage = codes.find((c) => c._id === newCall.stage);

    if (stage.label === "Completed") {
      newCall.completed_by = auth.user._id;
    }
    await actions.updateCall(newCall);
    // check if we can resolve the ticket
    if (
      stage.label === "Completed" &&
      isSupportTicket &&
      supportTicket?.request_status?.label !== "Resolved"
    ) {
      checkTicketCalls();
    } else {
      setEditing(false);
      onCancel();
    }
    setLoading(false);
  };

  const toggleShow = () => {
    setOpen((prev) => !prev);
  };

  const handleSaveWithRecurring = async (updated, recurrence) => {
    // If making a change to recurrence (i.e. date range), recreate calls,
    if (recurrence.dates) {
      const updatedCall = { ...call, ...updated };
      await actions.createRecurringCalls(updatedCall, recurrence);
    } else {
      // else perform bulk update
      const updatedCall = { ...call, ...updated };
      await actions.updateRecurringCalls(call.recurring_id, updatedCall);
    }
    await actions.getCall(id);
    setEditing(false);
  };

  // Ticket resolution
  const handleInput = (e) => {
    const { value } = e.target;
    setState((prev) => ({ ...prev, note: value }));
  };

  const handleResolve = () => {
    actions.showModalSuccess("resolveTicketModal");
  };

  const handleConfirmResolve = async () => {
    const request_status = codes.find(
      (code) =>
        code.entity === "Support Ticket" &&
        code.value === "R" &&
        code.field === "status"
    );

    const resolution = {
      resolved_by: auth.user._id,
      closing_note: state.note
    };

    const data = {
      ...supportTicket,
      request_status: request_status._id,
      resolution,
      modified_by_user_id: auth.user._id,
      additional_note: state.note,
      send_customer_email: sendCustomerEmail
    };

    await actions.updateSupportTicket(data);

    setEditing(false);
    actions.getCall(id);
    onCancel();
  };

  const handleCancelResolve = () => {
    setEditing(false);
    actions.getCall(id);
    onCancel();
  };

  const handleCheckbox = (e) => {
    setSendCustomerEmail(e?.target?.checked);
  };

  const callConfirmActions = (
    <span>
      <button className="btn btn-success" type="button" onClick={confirmSingle}>
        Single
      </button>
      <button className="btn btn-success" type="button" onClick={confirmSeries}>
        Series
      </button>
    </span>
  );

  const followUpAction = (
    <span>
      <button className="btn btn-success" type="button" onClick={followUpCall}>
        Follow up
      </button>
    </span>
  );

  const followUpAndConfirmAction = (
    <span>
      <button className="btn btn-success" type="button" onClick={handleResolve}>
        Resolve
      </button>
      <button className="btn btn-success" type="button" onClick={followUpCall}>
        Follow up
      </button>
    </span>
  );

  const confirmClose = (
    <div>
      <p>Would you like to resolve this ticket or create a follow up call?</p>
    </div>
  );

  const confirmResolve = (
    <div>
      <p>Are you sure you want to resolve this ticket?</p>
      <textarea
        maxLength="250"
        className="form-control"
        onChange={handleInput}
        placeholder="Note"
      />
      <small
        style={{
          float: "right",
          color: state?.note && state.note.length >= 250 ? "red" : ""
        }}
      >
        {state?.note ? `${state.note.length} / ` : ""}250 Character Limit
      </small>
      <div style={{ display: "flex", alignItems: "center", marginTop: "2rem" }}>
        <Checkbox
          name="sendCustomerEmail"
          checked={sendCustomerEmail}
          onClickCheck={handleCheckbox}
        />
        <div style={{ marginLeft: "1rem" }}>
          Send Customer Email
          <small style={{ marginLeft: "1rem" }}>
            *This includes the resolution note above.
          </small>
        </div>
      </div>
    </div>
  );

  const assigned_to = users.find((u) => u._id === call.assigned_to);

  const addCallForm = (
    <SupportTicketForm
      supportTicket={supportTicket}
      customers={customers}
      contracts={contracts}
      projects={projects}
      call={call}
      users={users}
      auth={auth}
      codes={codes}
      saving={savingCall}
      onSave={onSaveNewCall}
      onCancel={onCancel}
      editing
      adding
      isFollowUp={createFollowUp}
    />
  );

  if (isEmpty(call) || loading) {
    return (
      <div className="vertical-center -full">
        <Loading />
      </div>
    );
  }

  return (
    <React.Fragment>
      <HeaderWrapper
        breadcrumbs={[{ label: "Edit Call" }]}
        onClickEdit={
          (auth.maxRole > 2000 ||
            (assigned_to && assigned_to._id === auth.user._id)) &&
          call.stage &&
          (call.stage.order <= 4 ||
            auth.user.roles
              .map((role) => role.name)
              .includes("Billing Admin")) &&
          !(call.call_type?.value === "PERSONAL" && !auth.isAdmin) &&
          onClickEdit
        }
        isMobile={isMobile}
        icon={
          call.stage && (
            <Badge color={call.stage.color}>{call.stage.label}</Badge>
          )
        }
      />
      {call.support_ticket_id && supportTicket && (
        <SupportTicketDisplay
          supportTicket={supportTicket}
          toggleShow={toggleShow}
          open={open}
        />
      )}
      <div className="content-wrapper">
        {!isEmpty(projects) && (
          <CallForm
            call={call}
            contracts={contracts}
            users={users}
            auth={auth}
            customers={customers}
            projects={projects}
            services={services}
            codes={codes}
            onToggle={toggleShow}
            onSave={handleSave}
            onSaveWithRecurring={handleSaveWithRecurring}
            onCancel={onCancel}
            saving={savingCall || loading}
            editing={editing}
            recurring={recurring}
            clearProgressBarOnUnmount
          />
        )}
        <ConfirmModal
          id="callConfirmModal"
          title="Edit as Single Call or Series?"
          body="Do you wish to edit this a single occurrence or as a recurring series?"
          actions={callConfirmActions}
          close={actions.hideModal}
          confirm={onClickEdit}
        />
        <ConfirmModal
          id="resolveTicketModal"
          title="Resolve Ticket"
          body={confirmResolve}
          close={handleCancelResolve}
          confirm={handleConfirmResolve}
        />
        <ConfirmModal
          id="resolveOrFollowUpTicketModal"
          title="Update Ticket"
          body={confirmClose}
          actions={followUpAndConfirmAction}
          close={handleCancelResolve}
          cancelLabel="No"
        />
        <ConfirmModal
          id="createFollowUpCallModal"
          title="Call Completed"
          actions={followUpAction}
          body="Would you like to create a follow up call?"
          close={handleCancelResolve}
        />
        <Modal
          id="supportTicketModalAddCall"
          title="Add Call to Ticket"
          body={addCallForm}
          close={actions.hideModal}
        />
      </div>
    </React.Fragment>
  );
};
CallEditPage.propTypes = {
  actions: PropTypes.object,
  customers: PropTypes.array,
  contracts: PropTypes.array,
  projects: PropTypes.array,
  users: PropTypes.array,
  services: PropTypes.array,
  call: PropTypes.object,
  history: PropTypes.object,
  supportTicket: PropTypes.object,
  auth: PropTypes.object,
  codes: PropTypes.array,
  savingCall: PropTypes.bool,
  isMobile: PropTypes.bool
};

function mapStatesToProps(state) {
  return {
    state: state.reducers,
    auth: state.reducers.auth,
    customers: state.reducers.customers,
    call: state.reducers.call,
    savingCall: state.reducers.savingCall,
    recurringCalls: state.reducers.recurringCalls,
    contracts: state.reducers.contracts,
    projects: state.reducers.projects,
    users: state.reducers.users,
    codes: state.reducers.codes,
    services: state.reducers.services,
    supportTicket: state.reducers.supportTicket,
    router: state.router
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...modalActions,
        ...callActions,
        ...contractActions,
        ...userActions,
        ...customerActions,
        ...projectActions,
        ...serviceActions,
        ...codeActions,
        ...supportTicketActions
      },
      dispatch
    )
  };
}

export default connect(mapStatesToProps, mapDispatchToProps)(CallEditPage);
